import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import externalLinkIcon from 'core/assets/svg/external-link.svg';

import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import BaseDialog from 'core/components/base-dialog/base-dialog';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import CollapsibleCard from 'core/components/collapsible-card';
import { Tooltip } from 'core/components/tooltip';
import helpCircleIcon from 'core/assets/svg/help-circle.svg';
import { dateFormatter } from 'core/helpers/formatters';
import { track } from 'core/helpers/analytics';
import useStyles from './factors-impacting-price.styles';

const itemCountLabel = (count) => (count === 1 ? '1 item' : `${count} items`);

const COURT_RECORD_TOOLTIP =
  "Branch may identify violations from court or public records that other carriers don't display.";

const FactorItem = ({ factor, classes }) => (
  <div>
    <div className={classes.factorTitleRow}>
      <span className={factor.date ? classes.factorViolationTitle : classes.factorPropertyTitle}>{factor.title}</span>
      {factor.moreInfo && (
        <Tooltip text={factor.moreInfoTooltip} placement="top" smallIcon={{ src: helpCircleIcon }} iconSize="icon16" />
      )}
    </div>
    {factor.description && <p className={classes.factorDescription}>{factor.description}</p>}
    {factor.date && (
      <div className={classes.factorMetadata}>
        <span>{dateFormatter(factor.date)}</span>
        {factor.source && (
          <>
            <span>&bull;</span>
            <span>Source: {factor.source}</span>
            {factor.isCourtRecord && (
              <Tooltip
                text={COURT_RECORD_TOOLTIP}
                placement="top"
                smallIcon={{ src: helpCircleIcon }}
                iconSize="icon16"
              />
            )}
          </>
        )}
      </div>
    )}
  </div>
);

const FactorsImpactingPrice = observer(({ open, onClose, factors }) => {
  const classes = useStyles();
  const { offer: offerStore, quote: quoteStore } = useStore();
  const session = useSession();
  const offer = offerStore?.offer || quoteStore?.offer;

  const { offerId: offerIdFromURL } = useParams();
  const { protocol, host } = window.location;
  const offerId = offerIdFromURL || offer?.id;
  const detailsLink = `${protocol}//${host}/offer/${offerId}/details`;

  useEffect(() => {
    if (open) {
      track('Reports and Pricing details viewed', { offerId });
    }
  }, [open, offerId]);

  const homePropertyFactors = factors?.homeFactors?.filter((f) => f.isPropertyFactor) ?? [];
  const homeClaims = factors?.homeFactors?.filter((f) => f.date) ?? [];

  const redirectToCompleteOfferDetails = () => {
    window.open(detailsLink, '_blank');
  };

  return (
    <BaseDialog open={open} onClose={onClose} blockContent size="md">
      <Label type="greenBig" className={classes.bottomSpacing}>
        Factors impacting price and eligibility
      </Label>
      <Label type="infoSubtitleSmall" className={classes.bottomSpacing}>
        These details come from reports and other information about this offer. Not all items affect price or
        eligibility, and it can vary by state.
      </Label>

      {/* ----- HOME FACTORS ----- */}
      {factors?.homeFactors?.length > 0 && (
        <CollapsibleCard
          content={<Label type="infoSubtitle">Home</Label>}
          contentRight={<span className={classes.factorCount}>{itemCountLabel(factors.homeFactors.length)}</span>}
        >
          <div className={classes.cardContent}>
            {homePropertyFactors.length > 0 && (
              <div className={classes.subsection}>
                <p className={classes.subsectionTitle}>Property details</p>
                <div className={classes.contentBox}>
                  {homePropertyFactors.map((factor, index) => (
                    <React.Fragment key={`home-property-${index}`}>
                      {index > 0 && <div className={classes.factorDivider} />}
                      <FactorItem factor={factor} classes={classes} />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {homeClaims.length > 0 && (
              <div className={classes.subsection}>
                <p className={classes.subsectionTitle}>Home claims</p>
                <div className={classes.contentBox}>
                  {homeClaims.map((factor, index) => (
                    <React.Fragment key={`home-claim-${index}`}>
                      {index > 0 && <div className={classes.factorDivider} />}
                      <FactorItem factor={factor} classes={classes} />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CollapsibleCard>
      )}

      {/* ----- PEOPLE FACTORS ----- */}
      {factors?.peopleFactors && Object.keys(factors.peopleFactors).length > 0 && (
        <CollapsibleCard
          content={<Label type="infoSubtitle">People</Label>}
          contentRight={
            <span className={classes.factorCount}>
              {itemCountLabel(Object.values(factors.peopleFactors).reduce((sum, arr) => sum + arr.length, 0))}
            </span>
          }
        >
          <div className={classes.cardContent}>
            {Object.keys(factors.peopleFactors).map((key) => {
              const [firstName, lastName, dateOfBirth] = key.split('/');
              const people = offer.quote.drivers?.length ? offer.quote.drivers : offer.quote.people;
              const person = people?.find(
                (p) => p.firstName === firstName && p.lastName === lastName && p.dateOfBirth === dateOfBirth
              );

              if (!person) {
                return null;
              }

              return (
                <div key={key} className={classes.subsection}>
                  <div className={classes.personSubsectionHeader}>
                    <p className={classes.subsectionTitle}>{`${person.firstName} ${person.lastName}`}</p>
                    {person.isPrimary && <span className={classes.roleLabel}>Applicant</span>}
                    {person.isCoApplicant && <span className={classes.roleLabel}>Co-applicant</span>}
                  </div>
                  <div className={classes.contentBox}>
                    {factors.peopleFactors[key].map((factor, index) => (
                      <React.Fragment key={`person-factor-${index}`}>
                        {index > 0 && <div className={classes.factorDivider} />}
                        <FactorItem factor={factor} classes={classes} />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleCard>
      )}

      {/* ----- CARS FACTORS ----- */}
      {factors?.carFactors && Object.keys(factors.carFactors).length > 0 && (
        <CollapsibleCard
          content={<Label type="infoSubtitle">Cars</Label>}
          contentRight={
            <span className={classes.factorCount}>
              {itemCountLabel(Object.values(factors.carFactors).reduce((sum, arr) => sum + arr.length, 0))}
            </span>
          }
        >
          <div className={classes.cardContent}>
            {Object.keys(factors.carFactors).map((VIN) => {
              const car = offer.quote.cars.find((c) => c.VIN === VIN);

              if (!car) {
                return null;
              }

              return (
                <div key={VIN} className={classes.subsection}>
                  <p className={classes.carSubsectionTitle}>{`${car.make} ${car.model} ${car.year}`}</p>
                  <div className={classes.contentBox}>
                    {factors.carFactors[VIN].map((factor, index) => (
                      <React.Fragment key={`car-factor-${index}`}>
                        {index > 0 && <div className={classes.factorDivider} />}
                        <FactorItem factor={factor} classes={classes} />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleCard>
      )}

      <Grid container justifyContent="flex-end" spacing={2} className={classes.buttons}>
        {session.canSeeFullOfferDetails && (
          <Grid item>
            <Button variant="contained" color="secondary" onClick={redirectToCompleteOfferDetails}>
              <img src={externalLinkIcon} alt="" className={classes.buttonIcon} />
              View complete offer details
            </Button>
          </Grid>
        )}
        <Grid item>
          <Button variant="contained" color="primary" onClick={onClose}>
            Close
          </Button>
        </Grid>
      </Grid>
    </BaseDialog>
  );
});

export default FactorsImpactingPrice;
